import React, { useEffect, useRef, useMemo, useState } from "react";
import SearchInput from "./default-state/SearchInput";
import LastQueriesBlock from "./default-state/LastQueriesBlock";
import PopularQueriesBlock from "./default-state/PopularQueriesBlock";
import RecommendedProductsBlock from "./default-state/RecommendedProductsBlock";
import SearchResults from "./search-results/SearchResults";
import { useLiveSearch } from "../hooks/useLiveSearch";
import { getRecommendations } from "../search-core/recommendations";
import { addToHistory, clearSearchHistory } from "../search-core/history";
import { PopularQuery } from "../types/search";
import { isBitrixMode, buildPopularQueriesUrl } from "../utils/bitrixApiUrl";
import SuggestionsBlock from "./search-results/SuggestionsBlock";

export interface SearchModalContentProps {
	/** Whether the modal is open */
	isOpen: boolean;
	/** Callback to close the modal */
	onClose: () => void;
	/** Placeholder text for the search input */
	placeholder?: string;
	/** Optional title for the modal */
	title?: string;
	/** Optional CSS class name */
	className?: string;
}

/**
 * SearchModal - Modal dialog for search functionality
 *
 * This component provides a modal interface for search operations.
 * It's designed to be controlled by parent state and can be easily
 * extended with actual search logic.
 *
 * Key features:
 * - Controlled open/close state
 * - Escape key and backdrop click to close
 * - Focus management for accessibility
 * - Clean, extensible structure
 *
 * Future extensions:
 * - Portal rendering for proper DOM placement
 * - Animation on enter/exit
 * - Search history display
 * - Recent searches
 * - Filter controls
 * - Results preview
 */
const SearchModalContent: React.FC<SearchModalContentProps> = ({ isOpen, onClose, placeholder = "Что вы хотите найти?", className = "" }) => {
	const searchInputRef = useRef<HTMLInputElement>(null);

	// Stabilize search options to prevent unnecessary re-renders
	const searchOptions = useMemo(() => {
		// Get base URL from global config, env variable, or default Bitrix path
		let baseUrl = "/local/modules/live_search/api/"; // default Bitrix path
		if (typeof window !== "undefined" && window.LiveSearchConfig?.apiBasePath) {
			baseUrl = window.LiveSearchConfig.apiBasePath;
		} else if (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE_URL) {
			baseUrl = import.meta.env.VITE_API_BASE_URL;
		}
		// Ensure no trailing slash
		if (baseUrl.endsWith("/")) {
			baseUrl = baseUrl.slice(0, -1);
		}
		return {
			serviceType: "meilisearch" as const,
			debounceDelay: 350,
			autoSearch: true,
			serviceConfig: {
				baseUrl,
			},
		};
	}, []);

	// State for popular queries from server
	const [popularQueries, setPopularQueries] = useState<PopularQuery[]>([]);

	// Recommendations loaded during application bootstrap
	const recommendations = getRecommendations();

	// Load popular queries once on component mount (idle state)
	useEffect(() => {
		const loadPopularQueries = async () => {
			try {
				let endpoint;
				if (isBitrixMode()) {
					endpoint = buildPopularQueriesUrl();
				} else {
					// Non-Bitrix mode (development)
					let baseUrl = window.LiveSearchConfig?.apiBasePath;
					if (!baseUrl && typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE_URL) {
						baseUrl = import.meta.env.VITE_API_BASE_URL;
					}
					if (!baseUrl) {
						baseUrl = "/local/modules/live_search/api";
					}
					// Ensure no trailing slash
					if (baseUrl.endsWith("/")) {
						baseUrl = baseUrl.slice(0, -1);
					}
					endpoint = `${baseUrl}/api/popular-queries`;
				}
				const response = await fetch(endpoint);
				if (!response.ok) {
					throw new Error(`HTTP error: ${response.status}`);
				}
				const data = await response.json();
				// Map server data to PopularQuery type, using query as id
				const mappedQueries: PopularQuery[] = data.map((item: { query: string; count: number }) => ({
					id: item.query, // Use query as id (serves as React key)
					query: item.query,
					count: item.count,
				}));
				setPopularQueries(mappedQueries);
			} catch (error) {
				console.error("Failed to load popular queries:", error);
				// Silently fail - block won't be rendered
			}
		};

		loadPopularQueries();
	}, []);

	// Live search hook
	const { query, setQuery, results, normalizedResults, status, error, isLoading, hasError, isEmpty, filteredHistory, history, suggestions, search: performSearch, clear: clearSearch, loadMore, hasMore } = useLiveSearch(searchOptions);

	// Handle escape key to close modal
	useEffect(() => {
		const handleEscapeKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				onClose();
			}
		};

		if (isOpen) {
			document.addEventListener("keydown", handleEscapeKey);
			// Focus search input when modal opens
			setTimeout(() => {
				searchInputRef.current?.focus();
			}, 100);
		}

		return () => {
			document.removeEventListener("keydown", handleEscapeKey);
		};
	}, [isOpen, onClose]);

	// Prevent body scroll when modal is open
	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "";
		}

		return () => {
			document.body.style.overflow = "";
		};
	}, [isOpen]);

	const mouseDownOnBackdrop = React.useRef(false);

	const handleMouseDown = (event: React.MouseEvent) => {
		mouseDownOnBackdrop.current = event.target === event.currentTarget;
	};

	const handleMouseUp = (event: React.MouseEvent) => {
		if (mouseDownOnBackdrop.current && event.target === event.currentTarget) {
			onClose();
		}

		mouseDownOnBackdrop.current = false;
	};

	const handleSearchSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		// Trigger search with current query
		if (query.trim().length >= 2) {
			// Save to search history (client-side only)
			addToHistory(query);
			performSearch(query);
		}
	};

	const handleHistorySelect = (historyQuery: string) => {
		if (historyQuery === "") {
			// Clear history
			clearSearchHistory();
			// Trigger re-render by performing search again (preserves current search state)
			if (query.trim().length >= 2) {
				performSearch(query);
			}
			return;
		}
		// Set query and perform search without saving to history
		setQuery(historyQuery);
		performSearch(historyQuery);
	};

	const handleSuggestionClick = (suggestion: string) => {
		// Set query and perform search without saving to history
		setQuery(suggestion);
		performSearch(suggestion);
	};

	const combinedClassName = `search-modal ${className}`.trim();

	// Map search history to LastQuery format for LastQueriesBlock
	const lastQueries = React.useMemo(
		() =>
			history.map((item) => ({
				id: item.timestamp,
				query: item.query,
				timestamp: item.timestamp,
			})),
		[history],
	);

	const handleLastQueryClick = (query: string) => {
		setQuery(query);
		performSearch(query);
	};

	const handleLastQueryClear = () => {
		clearSearchHistory();
		// Force re-render to update history
		setQuery("");
	};

	const handlePopularQueryClick = (query: string) => {
		// Set query and perform search without saving to history
		setQuery(query);
		performSearch(query);
	};

	return (
		<div className="search-overlay" onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}>
			<div className="search-modal-overlay">
				<div className={combinedClassName}>
					<form onSubmit={handleSearchSubmit} className="search-modal-form">
						<SearchInput ref={searchInputRef} value={query} onChange={setQuery} onClear={clearSearch} placeholder={placeholder} />
						{/* Suggestions inline under input */}
						{suggestions && suggestions.length > 0 && <SuggestionsBlock suggestions={suggestions} onSuggestionClick={handleSuggestionClick} />}
					</form>
					<div className="search-modal-body">
						{query.trim() === "" ? (
							// Default state - shown when search field is empty
							<div className="search-empty-state">
								<div className="search-empty-state-left">
									{lastQueries.length > 0 && <LastQueriesBlock queries={lastQueries} onQueryClick={handleLastQueryClick} onClearClick={handleLastQueryClear} />}
									{popularQueries.length > 0 && <PopularQueriesBlock queries={popularQueries} onQueryClick={handlePopularQueryClick} />}
								</div>
								<div className="search-empty-state-right">
									<RecommendedProductsBlock products={recommendations} />
								</div>
							</div>
						) : (
							// Search state - shown when user is typing or has results
							<div className="search-results-state">
								<SearchResults hits={results} normalizedResults={normalizedResults} query={query} history={filteredHistory} suggestions={suggestions} onHistorySelect={handleHistorySelect} onSuggestionClick={handleSuggestionClick} isLoading={status === "typing" || isLoading} hasError={hasError} isEmpty={isEmpty} error={error} onRetry={() => performSearch(query)} loadMore={loadMore} hasMore={hasMore} />
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default SearchModalContent;
